// BOM Intelligence Dashboard
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Chip,
  Grid,
  Skeleton,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  alpha,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  LinearProgress,
} from '@mui/material';
import {
  AttachMoney as CostIcon,
  Inventory as AvailabilityIcon,
  CompareArrows as AlternativesIcon,
  Refresh as RefreshIcon,
  Psychology as IntelligenceIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Savings as SavingsIcon,
  VerifiedUser as ReadinessIcon,
  Science as ScenariosIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  SmartToy as AiIcon,
  QueryStats as AnalyticsIcon,
  LocalShipping as ShippingIcon,
  SwapHoriz as SwapIcon,
  Speed as SpeedIcon,
  Explore as ExploreIcon,
  Tune as SimulationIcon,
} from '@mui/icons-material';
import { KPICard } from '../../components/analytics';
import {
  useBomCostIntelligence,
  useBomAvailabilityIntelligence,
  useBomCostTrend,
  useBomAlternativesIntelligence,
  useBomReadinessScore,
  useBomReadinessTrend,
  useEnrichBomCost,
  useEnrichBomAvailability,
  useEnrichBomAlternatives,
  useCalculateReadiness,
  useEnrichAll,
  useEnrichmentStatus,
} from '../../hooks/useBomIntelligence';
import type { EnrichmentPhaseStatus } from '../../services/bomIntelligenceService';
import CostAnalysisTab from './tabs/CostAnalysisTab';
import AvailabilityTab from './tabs/AvailabilityTab';
import AlternativesTab from './tabs/AlternativesTab';
import ScenariosTab from './tabs/ScenariosTab';
import SimulationTab from './tabs/SimulationTab';
import ReadinessScoreCard from './components/ReadinessScoreCard';
import BlockersPanel from './components/BlockersPanel';
import BomMaterialExplorationPanel from '../../components/BomMaterialExplorationPanel';
import PartDetailDrawer from '../../components/PartDetailDrawer';
import { colors } from '../../theme/analyticsColors';
import { useAppDispatch, setBreadcrumbItems } from '@ciea.ai/smart-infra-navigation';
import { NavBackProvider } from '../../utils/navBack';

const PHASE_CONFIG = [
  { key: 'aiEnrichment', label: 'AI Classification', icon: AiIcon, cadOnly: true },
  { key: 'costIntelligence', label: 'Cost Analysis', icon: AnalyticsIcon, cadOnly: false },
  { key: 'availabilityIntelligence', label: 'Availability', icon: ShippingIcon, cadOnly: false },
  { key: 'alternativeDiscovery', label: 'Alternatives', icon: SwapIcon, cadOnly: false },
  { key: 'readinessScore', label: 'Readiness', icon: SpeedIcon, cadOnly: false },
] as const;

const formatCurrency = (value: number | undefined | null) => {
  if (value == null || isNaN(value)) return '$0';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
};

const BomIntelligenceDashboard: React.FC = () => {
  const { bomId } = useParams<{ bomId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const autoEnrichTriggered = useRef(false);

  // Data fetching
  const { data: costData, isLoading: costLoading, error: costError } = useBomCostIntelligence(bomId!);
  const { data: availData, isLoading: availLoading, error: availError } = useBomAvailabilityIntelligence(bomId!);
  const { data: altData, isLoading: altLoading, error: altError } = useBomAlternativesIntelligence(bomId!);
  const { data: trendData } = useBomCostTrend(bomId!);
  const { data: readinessData, isLoading: readinessLoading } = useBomReadinessScore(bomId!);
  const { data: readinessTrendData } = useBomReadinessTrend(bomId!);

  // Individual mutations (kept for selective refresh buttons)
  const enrichCost = useEnrichBomCost();
  const enrichAvailability = useEnrichBomAvailability();
  const enrichAlternatives = useEnrichBomAlternatives();
  const calculateReadiness = useCalculateReadiness();

  // Orchestrated enrich-all mutation (fire-and-forget, returns immediately)
  const enrichAll = useEnrichAll();
  const [enrichTriggered, setEnrichTriggered] = useState(false);
  const [partDrawerPartId, setPartDrawerPartId] = useState<string | null>(null);
  const [partDrawerTab, setPartDrawerTab] = useState<number | undefined>(undefined);

  // Poll enrichment status — always enabled so we detect in-progress on page load
  const enrichmentStatus = useEnrichmentStatus(bomId!, true);
  const isEnrichingFromServer = enrichmentStatus.data?.isEnriching ?? false;
  // Show enriching state immediately on click (enrichTriggered) OR from server poll
  const isEnriching = enrichTriggered || isEnrichingFromServer;

  // Clear local trigger once server confirms enrichment is running (or done)
  useEffect(() => {
    if (enrichTriggered && isEnrichingFromServer) {
      setEnrichTriggered(false); // Server took over — no need for local flag
    }
  }, [enrichTriggered, isEnrichingFromServer]);

  const handleEnrichAll = () => {
    if (!bomId || isEnriching) return; // Prevent concurrent runs
    setEnrichTriggered(true);
    enrichAll.mutate({ bomId });
  };

  // Auto-enrichment on arrival from BOM creation
  const hasData = (costData?.enrichedCount ?? 0) > 0 || (availData?.enrichedCount ?? 0) > 0 || (altData?.enrichedCount ?? 0) > 0;
  const locationState = location.state as { autoEnrich?: boolean; message?: string } | null;

  useEffect(() => {
    if (locationState?.message && !successMessage) {
      setSuccessMessage(locationState.message);
    }
  }, [locationState]);

  useEffect(() => {
    if (
      locationState?.autoEnrich &&
      bomId &&
      !autoEnrichTriggered.current &&
      !hasData &&
      !costLoading &&
      !availLoading
    ) {
      autoEnrichTriggered.current = true;
      setEnrichTriggered(true);
      enrichAll.mutate({ bomId });
    }
  }, [bomId, locationState, hasData, costLoading, availLoading]);

  // KPI values
  const totalBOMCost = costData?.totalBOMCost ?? 0;
  const totalBenchmarkCost = costData?.lineItemCosts?.reduce((sum, li) => {
    return sum + ((li.costIntelligence?.benchmarkPrice ?? li.unitCost) * (li.quantity || 1));
  }, 0) ?? 0;
  const benchmarkVariance = totalBenchmarkCost > 0 ? totalBOMCost - totalBenchmarkCost : 0;
  const benchmarkVariancePct = totalBenchmarkCost > 0
    ? ((benchmarkVariance / totalBenchmarkCost) * 100)
    : 0;

  const savingsPotential = costData?.lineItemCosts?.reduce((sum, li) => {
    return sum + ((li.costIntelligence?.savingsRange?.max ?? 0) * (li.quantity || 1));
  }, 0) ?? 0;

  const atRiskCount = availData?.rollup?.atRiskCount ?? 0;
  const bomName = costData?.bomName || availData?.bomName || 'BOM';
  const bomNumber = costData?.bomNumber || availData?.bomNumber || '';

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(setBreadcrumbItems([
      { label: 'Bill of Materials', path: '/bom' },
      { label: bomName, path: `/bom/${bomId}` },
      { label: 'Intelligence', path: location.pathname },
    ]));
  }, [dispatch, bomName, bomId, location.pathname]);

  if (!bomId) {
    return <Alert severity="error">No BOM ID provided</Alert>;
  }

  return (
    <NavBackProvider label={`${bomName} · Intelligence`}>
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Success message snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Enrichment progress indicator — modern milestone stepper */}
      {isEnriching && (() => {
        const s = enrichmentStatus.data?.enrichmentStatus;
        const isCad = enrichmentStatus.data?.sourceType === 'CAD_STEP' || enrichmentStatus.data?.sourceType === 'CAD_IGES';
        const phases = PHASE_CONFIG.filter(p => !p.cadOnly || isCad);
        const completedCount = s ? phases.filter(p => {
          const st = s[p.key as keyof typeof s];
          return st === 'COMPLETE' || st === 'FAILED' || st === 'SKIPPED';
        }).length : 0;
        const progressPct = s ? Math.round((completedCount / phases.length) * 100) : 0;

        return (
          <Card elevation={0} sx={{
            border: '1px solid',
            borderColor: colors.border,
            borderRadius: 2,
            mb: 3,
            bgcolor: colors.background,
            overflow: 'hidden',
          }}>
            <LinearProgress
              variant={s ? 'determinate' : 'indeterminate'}
              value={progressPct}
              color="secondary"
              sx={{ height: 3 }}
            />
            <CardContent sx={{ py: 2.5, px: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <CircularProgress size={22} color="secondary" />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
                    Intelligence Engine Running
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {s ? `${completedCount} of ${phases.length} phases complete` : 'Starting analysis...'}
                  </Typography>
                </Box>
              </Box>

              {s && (
                <Stepper alternativeLabel activeStep={completedCount} connector={
                  <StepConnector sx={{
                    '& .MuiStepConnector-line': {
                      borderColor: colors.chart.quaternary,
                      borderTopWidth: 2,
                    },
                    '&.Mui-active .MuiStepConnector-line, &.Mui-completed .MuiStepConnector-line': {
                      borderColor: colors.chart.success,
                    },
                  }} />
                }>
                  {phases.map((phase) => {
                    const status = s[phase.key as keyof typeof s] as EnrichmentPhaseStatus | undefined;
                    const Icon = phase.icon;
                    const isComplete = status === 'COMPLETE';
                    const isFailed = status === 'FAILED';
                    const isActive = status === 'IN_PROGRESS';

                    return (
                      <Step key={phase.key} completed={isComplete}>
                        <StepLabel
                          error={isFailed}
                          StepIconComponent={() => (
                            <Box sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: isComplete ? colors.chart.success
                                : isFailed ? colors.chart.error
                                : isActive ? colors.accent
                                : colors.chart.quaternary,
                              color: '#FFFFFF',
                              transition: 'all 0.3s ease',
                              ...(isActive && {
                                boxShadow: `0 0 0 4px ${alpha(colors.accent, 0.2)}`,
                              }),
                            }}>
                              {isComplete ? <CheckIcon sx={{ fontSize: 16 }} /> :
                               isFailed ? <ErrorIcon sx={{ fontSize: 16 }} /> :
                               isActive ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> :
                               <Icon sx={{ fontSize: 16 }} />}
                            </Box>
                          )}
                        >
                          <Typography variant="caption" sx={{
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? colors.accent
                              : isComplete ? colors.chart.success
                              : isFailed ? colors.chart.error
                              : colors.light,
                          }}>
                            {phase.label}
                          </Typography>
                        </StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IntelligenceIcon sx={{ color: colors.accent, fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {bomName}
              </Typography>
              {bomNumber && (
                <Chip
                  label={bomNumber}
                  size="small"
                  sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem', bgcolor: colors.surfaceAlt }}
                />
              )}
            </Box>
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 0.25 }}>
              BOM Intelligence Dashboard
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={enrichCost.isPending ? <CircularProgress size={14} /> : <RefreshIcon />}
            disabled={enrichCost.isPending}
            onClick={() => enrichCost.mutate({ bomId })}
          >
            Refresh Cost
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={enrichAvailability.isPending ? <CircularProgress size={14} /> : <RefreshIcon />}
            disabled={enrichAvailability.isPending}
            onClick={() => enrichAvailability.mutate({ bomId })}
          >
            Refresh Availability
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={enrichAlternatives.isPending ? <CircularProgress size={14} /> : <AlternativesIcon />}
            disabled={enrichAlternatives.isPending}
            onClick={() => enrichAlternatives.mutate({ bomId })}
          >
            Discover Alternatives
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={calculateReadiness.isPending ? <CircularProgress size={14} color="inherit" /> : <ReadinessIcon />}
            disabled={calculateReadiness.isPending}
            onClick={() => calculateReadiness.mutate({ bomId })}
          >
            Calculate Readiness
          </Button>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <KPICard
            title="Total BOM Cost"
            value={formatCurrency(totalBOMCost)}
            icon={<CostIcon />}
            iconColor={colors.chart.primary}
            loading={costLoading}
            trend={trendData ? {
              direction: trendData.trend === 'increasing' ? 'up' as const : trendData.trend === 'decreasing' ? 'down' as const : 'flat' as const,
              value: trendData.changePercent ?? 0,
              isPositive: (trendData.changePercent ?? 0) < 0,
            } : undefined}
            info="Sum of all line item costs including labor and overhead"
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <KPICard
            title="Benchmark Variance"
            value={formatCurrency(Math.abs(benchmarkVariance))}
            icon={benchmarkVariance > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
            iconColor={benchmarkVariance > 0 ? colors.error : colors.success}
            loading={costLoading}
            status={benchmarkVariance > 0 ? 'critical' : benchmarkVariance < 0 ? 'good' : undefined}
            description={benchmarkVariancePct !== 0 ? `${benchmarkVariancePct > 0 ? '+' : ''}${benchmarkVariancePct.toFixed(1)}% vs. benchmark` : undefined}
            info="Difference between actual BOM cost and fair market benchmark"
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <KPICard
            title="Savings Potential"
            value={formatCurrency(savingsPotential)}
            icon={<SavingsIcon />}
            iconColor={colors.success}
            loading={costLoading}
            status={savingsPotential > 0 ? 'good' : undefined}
            info="Maximum potential savings from cost optimization across all line items"
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <KPICard
            title="At-Risk Items"
            value={atRiskCount}
            icon={<WarningIcon />}
            iconColor={atRiskCount > 0 ? colors.error : colors.success}
            loading={availLoading}
            status={atRiskCount === 0 ? 'good' : atRiskCount <= 3 ? 'warning' : 'critical'}
            info="Line items with HIGH or CRITICAL risk level"
          />
        </Grid>
      </Grid>

      {/* Readiness Score Section */}
      {(readinessData || readinessLoading) && (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <ReadinessScoreCard
              readinessData={readinessData}
              trendData={readinessTrendData}
              loading={readinessLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <BlockersPanel
              bomId={bomId!}
              blockers={readinessData?.blockers ?? []}
              currentScore={readinessData?.overall ?? 0}
              loading={readinessLoading}
            />
          </Grid>
        </Grid>
      )}

      {/* Empty State */}
      {!costLoading && !availLoading && !hasData && (
        <Card elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <IntelligenceIcon sx={{ fontSize: 48, color: colors.light, mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
              No Intelligence Data Yet
            </Typography>
            <Typography sx={{ color: 'text.secondary', mb: 3, maxWidth: 400, mx: 'auto' }}>
              Run the intelligence analysis to enrich this BOM with cost benchmarks, availability status, and supplier risk data.
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              startIcon={isEnriching ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              disabled={isEnriching}
              onClick={handleEnrichAll}
            >
              {isEnriching ? 'Analyzing...' : 'Run Intelligence Analysis'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Error States */}
      {costError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load cost intelligence data. {(costError as Error).message}
        </Alert>
      )}
      {availError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load availability data. {(availError as Error).message}
        </Alert>
      )}
      {altError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load alternatives data. {(altError as Error).message}
        </Alert>
      )}

      {/* Tabs — always show (Material Exploration tab doesn't depend on enrichment) */}
      {(hasData || costLoading || availLoading || altLoading || true) && (
        <>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ mb: 3, borderBottom: `1px solid ${colors.border}` }}
          >
            <Tab icon={<CostIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Cost Analysis" />
            <Tab icon={<AvailabilityIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Availability" />
            <Tab icon={<AlternativesIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Alternatives" />
            <Tab icon={<ScenariosIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Scenarios" />
            <Tab icon={<SimulationIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Simulation" />
            <Tab icon={<ExploreIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Material Exploration" />
          </Tabs>

          {/* Tab Content */}
          {activeTab === 0 && (
            costLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
                <Grid container spacing={3}>
                  <Grid size={{ xs: 6 }}><Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} /></Grid>
                  <Grid size={{ xs: 6 }}><Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} /></Grid>
                </Grid>
              </Box>
            ) : costData ? (
              <CostAnalysisTab bomId={bomId} costData={costData} onViewPart={(partId) => { setPartDrawerPartId(partId); setPartDrawerTab(undefined); }} />
            ) : null
          )}

          {activeTab === 1 && (
            availLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
              </Box>
            ) : availData ? (
              <AvailabilityTab bomId={bomId} availabilityData={availData} onViewPart={(partId) => { setPartDrawerPartId(partId); setPartDrawerTab(undefined); }} />
            ) : null
          )}

          {activeTab === 2 && (
            altLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Grid container spacing={2}>
                  {[1, 2, 3, 4].map(i => (
                    <Grid key={i} size={{ xs: 6, md: 3 }}><Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} /></Grid>
                  ))}
                </Grid>
                <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
              </Box>
            ) : altData ? (
              <AlternativesTab bomId={bomId} alternativesData={altData} onViewPart={(partId) => { setPartDrawerPartId(partId); setPartDrawerTab(undefined); }} />
            ) : null
          )}

          {activeTab === 3 && (
            <ScenariosTab bomId={bomId} />
          )}

          {activeTab === 4 && (
            <SimulationTab
              bomId={bomId}
              onViewPart={(partId) => { setPartDrawerPartId(partId); setPartDrawerTab(undefined); }}
            />
          )}

          {activeTab === 5 && (
            <BomMaterialExplorationPanel
              bomId={bomId}
              onViewPart={(partId, tab) => {
                setPartDrawerPartId(partId);
                setPartDrawerTab(tab);
              }}
            />
          )}
        </>
      )}

      {/* Part Detail Drawer */}
      <PartDetailDrawer
        partId={partDrawerPartId}
        initialTab={partDrawerTab}
        backTo={location.pathname}
        backLabel={`${bomName} · Intelligence`}
        onClose={() => {
          setPartDrawerPartId(null);
          setPartDrawerTab(undefined);
        }}
      />
    </Box>
    </NavBackProvider>
  );
};

export default BomIntelligenceDashboard;
